import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { init, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TrendingUp, X } from 'lucide-react';
use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);
function formatTime(seconds) {
    if (!Number.isFinite(seconds))
        return '';
    if (seconds < 60)
        return seconds.toFixed(seconds % 1 === 0 ? 0 : 2).replace(/0+$/, '').replace(/\.$/, '');
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds - minutes * 60;
    const precision = remainder % 1 === 0 ? 0 : 2;
    return `${minutes}:${remainder.toFixed(precision).padStart(precision ? 5 : 2, '0')}`;
}
function formatImperial(inches) {
    if (!Number.isFinite(inches))
        return '';
    const feet = Math.floor(inches / 12);
    const remainingInches = inches - feet * 12;
    return `${feet}' ${Number(remainingInches.toFixed(2))}\"`;
}
function axisValue(value, type) {
    if (type === 'time')
        return formatTime(value);
    if (type === 'imperial')
        return formatImperial(value);
    return Number(value.toFixed(2)).toLocaleString('en-US');
}
function dateAxisLabel(dateTime) {
    return new Date(dateTime).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}
export function AthleteProgressionDialog({ athleteName, events, onClose }) {
    const [selectedEventId, setSelectedEventId] = useState(events[0]?.id ?? '');
    const chartElement = useRef(null);
    const chartViewport = useRef(null);
    const selectedEvent = events.find((event) => event.id === selectedEventId) ?? events[0];
    useEffect(() => {
        function closeOnEscape(event) {
            if (event.key === 'Escape')
                onClose();
        }
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [onClose]);
    useLayoutEffect(() => {
        if (!chartElement.current || !chartViewport.current || !selectedEvent)
            return;
        if (chartViewport.current)
            chartViewport.current.scrollLeft = 0;
        const chartNode = chartElement.current;
        const viewportNode = chartViewport.current;
        const sizeCanvas = () => {
            const resultSpacing = viewportNode.clientWidth <= 520 ? 96 : 112;
            const resultsWidth = Math.max(3, selectedEvent.points.length) * resultSpacing;
            chartNode.style.width = `${Math.max(viewportNode.clientWidth, resultsWidth)}px`;
        };
        sizeCanvas();
        const buildCategoryData = () => {
            const plotWidth = Math.max(1, chartNode.clientWidth - 86);
            const resultIntervals = Math.max(1, selectedEvent.points.length - 1);
            const subdivisions = Math.max(1, Math.ceil((plotWidth / 32 - 1) / resultIntervals));
            const categoryCount = resultIntervals * subdivisions + 1;
            const indexes = new Map();
            selectedEvent.points.forEach((_point, index) => indexes.set(index * subdivisions, index));
            const categoryValues = Array.from({ length: categoryCount }, (_value, index) => String(index));
            const values = Array.from({ length: categoryCount }, () => null);
            indexes.forEach((pointIndex, categoryIndex) => {
                values[categoryIndex] = { value: selectedEvent.points[pointIndex].value };
            });
            return { indexes, categoryValues, values };
        };
        let categoryData = buildCategoryData();
        const chart = init(chartElement.current);
        const option = {
            animationDuration: 350,
            grid: { top: 44, right: 26, bottom: 48, left: 60, containLabel: false },
            tooltip: {
                trigger: 'item',
                renderMode: 'richText',
                formatter: (params) => {
                    const dataIndex = Array.isArray(params) ? params[0]?.dataIndex : params.dataIndex;
                    const pointIndex = categoryData.indexes.get(Number(dataIndex));
                    const point = pointIndex == null ? null : selectedEvent.points[pointIndex];
                    if (!point)
                        return '';
                    const date = new Date(point.dateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    return [date, point.meetName, point.location, `Result: ${point.resultLabel}`].filter(Boolean).join('\n');
                },
            },
            xAxis: {
                type: 'category',
                data: categoryData.categoryValues,
                boundaryGap: true,
                axisLine: { lineStyle: { color: '#c9d5dd' } },
                axisTick: { show: false },
                axisLabel: {
                    color: '#6d7f8d',
                    interval: 0,
                    hideOverlap: false,
                    formatter: (_value, index) => {
                        const pointIndex = categoryData.indexes.get(index);
                        return pointIndex == null ? '' : dateAxisLabel(selectedEvent.points[pointIndex]?.dateTime ?? 0);
                    },
                },
                splitLine: { show: false },
            },
            yAxis: {
                type: 'value',
                scale: true,
                inverse: false,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { color: '#6d7f8d', formatter: (value) => axisValue(value, selectedEvent.valueType) },
                splitLine: { lineStyle: { color: '#e7edf1' } },
            },
            series: [
                {
                    type: 'line',
                    data: categoryData.values,
                    connectNulls: true,
                    symbol: 'circle',
                    symbolSize: 10,
                    showSymbol: true,
                    smooth: 0.16,
                    lineStyle: { width: 3, color: '#0f568c' },
                    areaStyle: { color: 'rgba(84, 166, 222, .28)' },
                    itemStyle: { color: '#0f568c', borderColor: '#dceef9', borderWidth: 4 },
                    emphasis: { scale: 1.35 },
                    label: {
                        show: true,
                        position: 'top',
                        distance: 7,
                        color: '#17374f',
                        fontSize: 10,
                        fontWeight: 700,
                        formatter: (params) => {
                            const pointIndex = categoryData.indexes.get(Number(params.dataIndex));
                            return pointIndex == null ? '' : selectedEvent.points[pointIndex]?.resultLabel ?? '';
                        },
                    },
                },
            ],
        };
        chart.setOption(option);
        chart.on('click', (params) => chart.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: params.dataIndex }));
        const resizeObserver = new ResizeObserver(() => {
            sizeCanvas();
            categoryData = buildCategoryData();
            chart.setOption({ xAxis: { data: categoryData.categoryValues }, series: [{ data: categoryData.values }] });
            chart.resize();
        });
        resizeObserver.observe(viewportNode);
        return () => {
            resizeObserver.disconnect();
            chart.dispose();
        };
    }, [selectedEvent]);
    if (!selectedEvent)
        return null;
    return (_jsx("div", { className: "athlete-progression-backdrop", role: "presentation", onMouseDown: onClose, children: _jsxs("section", { className: "athlete-progression-dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": "athlete-progression-title", onMouseDown: (event) => event.stopPropagation(), children: [_jsx("button", { className: "athlete-progression-dialog__close", type: "button", onClick: onClose, "aria-label": "Close progression chart", children: _jsx(X, { size: 20 }) }), _jsxs("header", { children: [_jsx("span", { children: _jsx(TrendingUp, { size: 22 }) }), _jsxs("div", { children: [_jsx("h2", { id: "athlete-progression-title", children: "Progression" }), _jsx("p", { children: athleteName })] })] }), _jsx("div", { className: "athlete-progression-events", "aria-label": "Choose an event", children: events.map((event) => (_jsx("button", { type: "button", className: event.id === selectedEvent.id ? 'active' : '', onClick: () => setSelectedEventId(event.id), children: event.name }, event.id))) }), _jsxs("div", { className: "athlete-progression-chart-heading", children: [_jsx("strong", { children: selectedEvent.name }), _jsxs("span", { children: [selectedEvent.points.length, " results"] })] }), _jsx("div", { className: "athlete-progression-chart-viewport", ref: chartViewport, children: _jsx("div", { className: "athlete-progression-chart", ref: chartElement, role: "img", "aria-label": `${selectedEvent.name} progression chart from past to present` }) }), _jsx("p", { className: "athlete-progression-hint", children: "Select a point to view its date, meet, and location." })] }) }));
}
